import { useMemo } from 'react';

import { useShowStoreCause, useShowStoreItem } from '@givebacks/api/store';
import { useUserAccessPermissions } from 'core/Api';

import { useShowDonationStoreItem } from '../api/CrowdFunding/Management/donations/donations';

type AccountCodeFeatures = 'donation' | 'product_fundraiser' | 'product';

type UseStoreAccountCodes = {
  feature?: AccountCodeFeatures;
  causeId: string;
  uuid?: string;
};

const useStoreAccountCodes = ({ feature, causeId, uuid }: UseStoreAccountCodes) => {
  const { data: permissions, isLoading: isPermissionsLoading } = useUserAccessPermissions({
    causeId,
    accessType: 'assign_account_code',
  });
  const { data: donationStoreItem, isLoading: isDonationStoreItemLoading } =
    useShowDonationStoreItem({ causeId });

  const { data: storeItem, isLoading: isStoreItemLoading } = useShowStoreItem({
    causeId: causeId,
    uuid: uuid,
  });

  const { data: storeData, isLoading: isStoreCauseLoading } = useShowStoreCause({
    causeId: causeId,
  });

  const hasAccountCode = !!useMemo(() => {
    switch (feature) {
      case 'donation':
        return donationStoreItem?.account_code;
      case 'product_fundraiser':
        return storeData?.product_fundraisers_account_code;
      default:
        return storeItem?.account_code;
    }
  }, [
    donationStoreItem?.account_code,
    feature,
    storeData?.product_fundraisers_account_code,
    storeItem?.account_code,
  ]);

  const hasWriteAccess = useMemo(() => permissions?.write, [permissions]);

  /** Returns the appropriate label based on item and role access or returns false */
  const accountCodeActionLabel = useMemo(() => {
    if (hasWriteAccess) {
      return 'Edit Account Code';
    } else {
      return 'View Account Code';
    }
  }, [hasWriteAccess]);

  const isLoading = useMemo(() => {
    return (
      isDonationStoreItemLoading ||
      isStoreItemLoading ||
      isStoreCauseLoading ||
      isPermissionsLoading
    );
  }, [isDonationStoreItemLoading, isStoreItemLoading, isStoreCauseLoading, isPermissionsLoading]);

  return {
    hasAccountCode,
    hasWriteAccess,
    accountCodeActionLabel,
    isLoading,
  };
};

export { useStoreAccountCodes };
